import { useEffect, useState } from "react";
import { Button, Form, Input, Space, TableColumnsType, message } from "antd";
import Table, { TableProps } from "antd/es/table";
import TenantUserApi from "../../../../../../../api/TenantUserApi";
import { CreateTenantRoleWizardWrapper, FormWrapper, OperationWrapper, RolePermissionsWrapper, Title, TitleWrapper } from "./CreateTenantRoleWizard.styled";

interface CreateRoleWizardProps {
    tenantId: string,
    editingRoleId?: string,

    onClose: ()=> void,
    onComplete: ()=> void
}

const CreateTenantRoleWizard: React.FC<CreateRoleWizardProps> = (props)=> {
    // api
    let userApi = new TenantUserApi();
    const [messageApi, contextHolder] = message.useMessage();

    // form
    const [form] = Form.useForm();

    // state
    let [role, setRole] = useState<any>({name: '', remarks: ''});
    let [rolePermissions, setRolePermissions] = useState([]);
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [tenantRolePermissionCodePaths, setTenantRolePermissionCodePaths] = useState([]);
    let [tenantUserPermissionMetas, setTenantUserPermissionMetas] = useState<any[]>([]);
    let [loading, setLoading] = useState<boolean>(false);
    

    // functions
    let loadRoleAndPermissions = async ()=> {
        // tenant role
        let role = (await userApi.getTenantRole(props.tenantId, props.editingRoleId)).data.data;
        form.setFieldsValue(role);
        setRole(role);
        setSubmitEnabled(true);

        // tenant role permissions
        let rolePermissions = (await userApi.getTenantRolePermissions(props.tenantId, props.editingRoleId)).data.data;
        let tenantRolePermissionCodePaths = rolePermissions.map((permission)=> permission.permissionCodePath);
        setRolePermissions(rolePermissions);
        setTenantRolePermissionCodePaths(tenantRolePermissionCodePaths);
    };

    let loadPermissionMetas = async ()=> {
        // tenant user permission metas
        let tenantUserPermissionMetas = (await userApi.getTenantUserPermissionMetas()).data.data;
        setTenantUserPermissionMetas(tenantUserPermissionMetas);
    }

    // event
    let onNameChange = (e)=> {
        role.name = e.target.value;
        validateForm();
    };

    let onRemarksChange = (e)=> {
        role.remarks = e.target.value;
        validateForm();
    };

    let onSubmit = async ()=> {
        // save
        if (props.editingRoleId) {
            await userApi.updateTenantRole(props.tenantId, role);
        } else {
            role = (await userApi.createTenantRole(props.tenantId, role)).data.data;
            setRole(role);
        }

        // set role permissions
        await (userApi.setTenantRolePermissions(props.tenantId, role.id, tenantRolePermissionCodePaths));
        messageApi.success('保存成功');

        // callback
        props.onComplete();
        props.onClose();
    };

    let onCancel = ()=> {
        props.onClose();
    };

    // validate
    let validateForm = ()=> {
        setRole({...role});
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    }

    // effect
    useEffect(()=> {
        loadPermissionMetas();
        if (props.editingRoleId) {
            loadRoleAndPermissions();
        }
    }, [props.editingRoleId])

    // render
    
    const rowSelection: TableProps<TenantUserPermissionMeta>['rowSelection'] = {
        type: 'checkbox',
        selectedRowKeys: tenantRolePermissionCodePaths,
        onSelect: (record, selected, selectedRows) => {
            let visitTenantFeatureMeta = (tenantUserPermissionMeta: TenantUserPermissionMeta, onVisit: (tenantUserPermissionMeta: TenantUserPermissionMeta)=> void)=> {
                onVisit(tenantUserPermissionMeta);
                if (tenantUserPermissionMeta.children) {
                    for (let child of tenantUserPermissionMeta.children) {
                        visitTenantFeatureMeta(child, onVisit);
                    }
                }
                return;
            };

            let visitTenantUserPermissionMetas = (onVisit: (TenantUserPermissionMeta: TenantUserPermissionMeta)=> void)=> {
                for(let tenantUserPermissionMeta of tenantUserPermissionMetas) {
                    visitTenantFeatureMeta(tenantUserPermissionMeta, onVisit);
                }
            }

            let newTenantRolePermissionCodePaths = [].concat(tenantRolePermissionCodePaths);
            if (selected) {
                visitTenantUserPermissionMetas((tenantUserPermissionMeta)=> {
                    if (tenantUserPermissionMeta.codePath.indexOf(record.codePath) == 0 && newTenantRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath) < 0) {
                        newTenantRolePermissionCodePaths.push(tenantUserPermissionMeta.codePath);
                    }

                    if (record.codePath.indexOf(tenantUserPermissionMeta.codePath) == 0 && newTenantRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath) < 0) {
                        newTenantRolePermissionCodePaths.push(tenantUserPermissionMeta.codePath);
                    }
                });
            } else {
                visitTenantUserPermissionMetas((tenantUserPermissionMeta)=> {
                    if (tenantUserPermissionMeta.codePath.indexOf(record.codePath) == 0 && newTenantRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath) >= 0) {
                        let index = newTenantRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath);
                        newTenantRolePermissionCodePaths.splice(index, 1);
                    }
                });
            }
            setTenantRolePermissionCodePaths(newTenantRolePermissionCodePaths);
        },
        getCheckboxProps: (record: TenantUserPermissionMeta) => ({
            name: record.codePath,
        }),
    };

    const columns: TableColumnsType<any> = [
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: '300px',
            align: 'left',
            render: (text, record, index)=> {
                return <b>{record.name}</b>
            }
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            align: 'left',
            render: (text, record, index)=> {
                return <span>{record.description}</span>
            }
        },
    ];

    return (
        <CreateTenantRoleWizardWrapper>
            <TitleWrapper>
                <Title>{props.editingRoleId ? '编辑角色' : '新增角色'}</Title>
            </TitleWrapper>

            <FormWrapper>
                <Form form={form} name="validateOnly" layout="vertical">
                    <Form.Item name="name" label="角色名称" rules={[{required: true}]} initialValue={role.name} style={{"flex": "0 0 auto"}}>
                        <Input value={role.name} onChange={onNameChange} placeholder="请输入用户名" />
                    </Form.Item>

                    <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={role.remarks} style={{"flex": "0 0 auto"}}>
                        <Input.TextArea style={{height: '100px'}} onChange={onRemarksChange} placeholder="请输入用户备注"/>
                    </Form.Item>

                    <Form.Item name="permissions" label="角色权限" rules={[{required: false}]} style={{"flex": "0 0 30px", "marginBottom": "0px", "overflow": "hidden"}}>
                    </Form.Item>

                    <RolePermissionsWrapper>
                        <Table
                            rowKey={(record)=> record.codePath}
                            columns={columns} 
                            rowSelection={rowSelection}
                            dataSource={tenantUserPermissionMetas}
                            loading={loading}
                            pagination={false}
                        />
                    </RolePermissionsWrapper>
                </Form>
            </FormWrapper>

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </CreateTenantRoleWizardWrapper>
    )
}

interface TenantUserPermissionMeta {
    id: number;
    name: string;
    description: string;
    code: string;
    codePath: string;
    children: TenantUserPermissionMeta[];
}

export default CreateTenantRoleWizard;

